import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import StoreNavigation from '../components/StoreNavigation';
import StoreFooter from '../components/StoreFooter';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Truck, Shield, RefreshCcw, Loader2 } from 'lucide-react';

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, getCartItemCount, proceedToCheckout, isCheckingOut } = useCart();
  const { toast } = useToast();
  const [checkoutError, setCheckoutError] = useState(null);

  const subtotal = getCartTotal();
  const shipping = 15; // Flat rate shipping
  const total = subtotal + shipping;

  const handleCheckout = async () => {
    try {
      setCheckoutError(null);
      await proceedToCheckout();
    } catch (error) {
      setCheckoutError(error.message);
      toast({
        title: "Checkout Error",
        description: error.message || "Unable to proceed to checkout. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        <StoreNavigation cartItemCount={0} />
        
        <div className="pt-32 pb-24">
          <div className="container mx-auto px-6">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-24 h-24 bg-[#F3CEB2]/30 rounded-full flex items-center justify-center mx-auto mb-8">
                <ShoppingBag className="w-12 h-12 text-[#004D51]" />
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-[#004D51] mb-4">
                Your Cart is Empty
              </h1>
              <p className="text-xl text-[#545759] mb-8">
                Ready to gear up? Check out our professional refinishing suits designed for women.
              </p>
              <Link to="/shop">
                <Button className="h-14 px-8 bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black text-lg rounded-lg" data-testid="continue-shopping-btn">
                  Start Shopping <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <StoreFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <StoreNavigation cartItemCount={getCartItemCount()} />

      <div className="pt-32 pb-24">
        <div className="container mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-black text-[#004D51] mb-8">
            Your Cart
          </h1>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="space-y-6">
                {cartItems.map((item, index) => (
                  <div 
                    key={`${item.id}-${item.selectedSize}-${item.selectedColor?.name}-${index}`}
                    className="bg-white border-4 border-[#004D51] rounded-xl p-6 flex flex-col sm:flex-row gap-6"
                    data-testid={`cart-item-${item.id}`}
                  >
                    {/* Product Image */}
                    <Link to={`/product/${item.slug}`} className="flex-shrink-0">
                      <div className="w-32 h-32 bg-gray-100 rounded-lg overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </Link>

                    {/* Product Details */}
                    <div className="flex-1">
                      <Link to={`/product/${item.slug}`}>
                        <h3 className="text-xl font-black text-[#004D51] hover:text-[#DB5A14] transition-colors mb-2">
                          {item.name}
                        </h3>
                      </Link>
                      
                      <div className="text-sm text-[#545759] space-y-1 mb-4">
                        {item.selectedSize !== 'One Size' && (
                          <p>Size: <span className="font-bold">{item.selectedSize}</span></p>
                        )}
                        {item.selectedColor && (
                          <p className="flex items-center">
                            Color: 
                            <span 
                              className="inline-block w-4 h-4 rounded-full ml-2 mr-1 border border-gray-300" 
                              style={{ backgroundColor: item.selectedColor.hex }}
                            ></span>
                            <span className="font-bold">{item.selectedColor.name}</span>
                          </p>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        {/* Quantity Controls */}
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => updateQuantity(item.id, item.selectedSize, item.selectedColor?.name, item.quantity - 1)}
                            className="w-10 h-10 bg-gray-200 hover:bg-[#F3CEB2] rounded-lg font-black text-lg transition-all flex items-center justify-center"
                            data-testid={`decrease-qty-${item.id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="text-xl font-black text-[#004D51] min-w-8 text-center" data-testid={`qty-${item.id}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.selectedSize, item.selectedColor?.name, item.quantity + 1)}
                            className="w-10 h-10 bg-gray-200 hover:bg-[#F3CEB2] rounded-lg font-black text-lg transition-all flex items-center justify-center"
                            data-testid={`increase-qty-${item.id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Price & Remove */}
                        <div className="flex items-center gap-4">
                          <div className="text-xl font-black text-[#004D51]">
                            ${(item.price * item.quantity).toFixed(2)}
                          </div>
                          <button
                            onClick={() => removeFromCart(item.id, item.selectedSize, item.selectedColor?.name)}
                            className="text-[#545759] hover:text-red-500 transition-colors"
                            data-testid={`remove-item-${item.id}`}
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Continue Shopping Link */}
              <div className="mt-8">
                <Link 
                  to="/shop" 
                  className="text-[#DB5A14] hover:text-[#F39C49] font-bold flex items-center"
                >
                  <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
                  Continue Shopping
                </Link>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-[#F3CEB2]/20 border-4 border-[#004D51] rounded-xl p-6 sticky top-28">
                <h2 className="text-2xl font-black text-[#004D51] mb-6">Order Summary</h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-[#545759]">
                    <span>Subtotal ({getCartItemCount()} items)</span>
                    <span className="font-bold">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#545759]">
                    <span>Shipping</span>
                    <span className="font-bold">${shipping.toFixed(2)}</span>
                  </div>
                  <div className="border-t-2 border-[#004D51] pt-4 flex justify-between">
                    <span className="text-xl font-black text-[#004D51]">Total</span>
                    <span className="text-xl font-black text-[#004D51]">${total.toFixed(2)} AUD</span>
                  </div>
                </div>

                <Button 
                  onClick={handleCheckout}
                  disabled={isCheckingOut}
                  className="w-full h-14 bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black text-lg rounded-lg mb-4 disabled:opacity-70"
                  data-testid="checkout-btn"
                >
                  {isCheckingOut ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Redirecting to Checkout...
                    </>
                  ) : (
                    'Proceed to Checkout'
                  )}
                </Button>

                {checkoutError && (
                  <p className="text-sm text-red-500 text-center mb-4">
                    {checkoutError}
                  </p>
                )}

                <p className="text-xs text-center text-[#545759] mb-6">
                  Secure checkout powered by Shopify
                </p>

                {/* Trust Badges */}
                <div className="grid grid-cols-2 gap-4 pt-6 border-t border-gray-300">
                  <div className="text-center">
                    <RefreshCcw className="w-6 h-6 mx-auto mb-1 text-[#004D51]" />
                    <p className="text-xs font-bold text-[#545759]">Easy</p>
                    <p className="text-xs text-[#545759]">Returns</p>
                  </div>
                  <div className="text-center">
                    <Shield className="w-6 h-6 mx-auto mb-1 text-[#004D51]" />
                    <p className="text-xs font-bold text-[#545759]">Secure</p>
                    <p className="text-xs text-[#545759]">Payment</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StoreFooter />
    </div>
  );
};

export default Cart;
